import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../../Components/Dashboard/DashboardLayout/DashboardLayout';
import { useFacility } from '../../context/FacilityContext';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase/config';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  serverTimestamp,
  doc,
  deleteDoc,
  getDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
} from '@mui/material';
import Rating from '@mui/material/Rating';
import * as XLSX from 'xlsx';
import facilitiesData from '../../config/facilities.json';
import { canAccessFacility } from '../../config/facilityAccess';
import { uploadFileToS3, deleteFileFromS3 } from '../../utils/s3Operations';
import ListAltIcon from '@mui/icons-material/ListAlt';
import UploadIcon from '@mui/icons-material/Upload';
import SettingsIcon from '@mui/icons-material/Settings';
import styles from './Testimonials.module.css';
import TestimonialSettingsDialog from '../../Components/Testimonials/TestimonialSettingsDialog';

function Testimonials() {
  const { facilityId } = useFacility();
  const { currentUser } = useAuth();
  const formRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState('all');
  const [allTestimonials, setAllTestimonials] = useState([]);
  const [displayedTestimonials, setDisplayedTestimonials] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [openSettingsDialog, setOpenSettingsDialog] = useState(false);
  const [ratingEmails, setRatingEmails] = useState([]);
  const [editTestimonial, setEditTestimonial] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editRating, setEditRating] = useState(0);
  const [claims, setClaims] = useState({});
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortOption, setSortOption] = useState('Building');

  const isSuperAdmin = currentUser && claims.superadmin === true;
  const isAdmin = currentUser && claims.admin === true;
  const isStaff = currentUser && claims.staff === true;

  const canUploadTo = (fid) =>
    isSuperAdmin ||
    ((isAdmin || isStaff) && canAccessFacility(claims, fid));

  const canEditTestimonial = (t) => {
    if (isSuperAdmin) return true;
    const inAssignedBuilding = canAccessFacility(claims, t.facilityId);
    if (isAdmin && inAssignedBuilding) return true;
    if (
      isStaff &&
      inAssignedBuilding &&
      currentUser &&
      t.userId === currentUser.uid
    )
      return true;
    return false;
  };

  useEffect(() => {
    if (currentUser && currentUser.getIdTokenResult) {
      currentUser.getIdTokenResult().then((idTokenResult) => {
        setClaims(idTokenResult.claims);
      });
    }
  }, [currentUser]);

  const canRate =
    currentUser &&
    ratingEmails
      .map((e) => e.toLowerCase())
      .includes(currentUser.email.toLowerCase());

  useEffect(() => {
    const settingsRef = doc(db, 'testimonialSettings', 'global');
    getDoc(settingsRef)
      .then((docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setRatingEmails(data.ratingEmails || []);
        }
      })
      .catch((error) => {
        console.error('Error fetching testimonial settings:', error);
      });
  }, []);

  const handleOpenForm = () => {
    if (!canUploadTo(facilityId)) {
      alert("You don't have permission to upload a testimonial here.");
      return;
    }
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setTitle('');
    setDescription('');
    setVideoFile(null);
    setVideoDuration(0);
  };

  const handleFacilityChange = (event) => {
    setSelectedFacility(event.target.value);
  };

  useEffect(() => {
    setIsLoading(true);
    let testimonialsQuery = collection(db, 'testimonials');
    if (selectedFacility !== 'all') {
      testimonialsQuery = query(
        testimonialsQuery,
        where('facilityId', '==', selectedFacility),
      );
    }
    const unsubscribe = onSnapshot(testimonialsQuery, (snapshot) => {
      const fetched = [];
      snapshot.forEach((docSnap) => {
        fetched.push({ id: docSnap.id, ...docSnap.data() });
      });
      setAllTestimonials(fetched);
      setDisplayedTestimonials(fetched);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, [selectedFacility]);

  useEffect(() => {
    let filtered = [...allTestimonials];
    if (startDate) {
      const sd = new Date(startDate);
      filtered = filtered.filter((t) => t.createdAt?.toDate() >= sd);
    }
    if (endDate) {
      const ed = new Date(endDate);
      filtered = filtered.filter((t) => t.createdAt?.toDate() <= ed);
    }
    setDisplayedTestimonials(filtered);
  }, [startDate, endDate, allTestimonials]);

  const handleSortChange = (e) => {
    setSortOption(e.target.value);
  };

  const sortedTestimonials = () => {
    let sorted = [...displayedTestimonials];
    if (sortOption === 'Building') {
      sorted.sort((a, b) => {
        const aName =
          facilitiesData.find((f) => f.id === a.facilityId)?.name ||
          a.facilityId;
        const bName =
          facilitiesData.find((f) => f.id === b.facilityId)?.name ||
          b.facilityId;
        return aName.localeCompare(bName);
      });
    } else if (sortOption === 'Date') {
      sorted.sort(
        (a, b) => (b.createdAt?.toDate() || 0) - (a.createdAt?.toDate() || 0),
      );
    } else if (sortOption === 'Top') {
      sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    }
    return sorted;
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setVideoFile(file);
      const objectUrl = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.src = objectUrl;
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(objectUrl);
        setVideoDuration(video.duration);
      };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!videoFile) {
      alert('Please select a video file');
      return;
    }
    if (!canUploadTo(facilityId)) {
      alert("You don't have permission to upload a testimonial here.");
      return;
    }
    try {
      setIsLoading(true);
      const { url: uploadedFileUrl, key } = await uploadFileToS3(
        videoFile,
        'testimonials',
      );
      await addDoc(collection(db, 'testimonials'), {
        title,
        description,
        videoUrl: uploadedFileUrl,
        s3Key: key,
        facilityId,
        createdAt: serverTimestamp(),
        userId: currentUser ? currentUser.uid : null,
        ratings: {},
        rating: 0,
        duration: videoDuration,
      });
      handleCloseForm();
    } catch (error) {
      console.error('Error submitting testimonial:', error);
      alert('Error submitting testimonial. Check console for details.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (testimonial) => {
    try {
      const response = await fetch(testimonial.videoUrl, { mode: 'cors' });
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const fileUrl = window.URL.createObjectURL(blob);
      const fileNameWithExtension = testimonial.videoUrl
        .split('/')
        .pop()
        .split('?')[0];
      const extension = fileNameWithExtension.split('.').pop();
      let downloadName = testimonial.title;
      if (!downloadName.toLowerCase().endsWith(`.${extension.toLowerCase()}`)) {
        downloadName += `.${extension}`;
      }
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(fileUrl);
    } catch (error) {
      console.error('Download error:', error);
      alert('Error downloading file.');
    }
  };

  const handleEditTestimonial = (testimonial) => {
    if (!canEditTestimonial(testimonial)) {
      alert("You don't have permission to edit this testimonial.");
      return;
    }
    setEditTestimonial(testimonial);
    setEditTitle(testimonial.title);
    setEditDescription(testimonial.description);
    const userRating =
      testimonial.ratings && currentUser && testimonial.ratings[currentUser.uid]
        ? testimonial.ratings[currentUser.uid]
        : 0;
    setEditRating(userRating);
  };

  const handleSaveEdit = async () => {
    if (!editTestimonial) return;
    try {
      setIsLoading(true);
      const testimonialRef = doc(db, 'testimonials', editTestimonial.id);
      let updatedData = { title: editTitle, description: editDescription };
      if (canRate && currentUser) {
        const currentRatings = editTestimonial.ratings || {};
        currentRatings[currentUser.uid] = editRating;
        const ratingValues = Object.values(currentRatings);
        const avg =
          ratingValues.reduce((sum, val) => sum + val, 0) / ratingValues.length;
        const roundedAvg = Math.floor(avg * 2) / 2;
        updatedData = {
          ...updatedData,
          ratings: currentRatings,
          rating: roundedAvg,
        };
      }
      await updateDoc(testimonialRef, updatedData);
      setEditTestimonial(null);
    } catch (error) {
      console.error('Error updating testimonial:', error);
      alert('Error saving changes. Check console for details.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteEdit = async () => {
    if (!editTestimonial) return;
    if (!canEditTestimonial(editTestimonial)) {
      alert("You don't have permission to delete this testimonial.");
      return;
    }
    if (!window.confirm('Are you sure you want to delete this testimonial?'))
      return;
    try {
      setIsLoading(true);
      await deleteFileFromS3(editTestimonial.s3Key, 'testimonials');
      await deleteDoc(doc(db, 'testimonials', editTestimonial.id));
      setEditTestimonial(null);
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      alert('Error deleting testimonial. Check console for details.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDuration = (durationSeconds) => {
    if (!durationSeconds) return '0:00';
    const minutes = Math.floor(durationSeconds / 60);
    const seconds = Math.floor(durationSeconds % 60)
      .toString()
      .padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  const handleGenerateReport = async () => {
    try {
      setIsLoading(true);
      let filtered = [...displayedTestimonials];
      const data = [];
      data.push(['Building', 'Date', 'Title', 'Length', 'Rating']);
      const getFacilityName = (fId) => {
        const fac = facilitiesData.find((f) => f.id === fId);
        return fac ? fac.name : fId;
      };
      const formatDate = (ts) =>
        ts?.toDate ? ts.toDate().toLocaleDateString() : 'Pending';
      const facilityMap = {};
      filtered.forEach((t) => {
        const fId = t.facilityId || 'Unknown';
        if (!facilityMap[fId]) facilityMap[fId] = [];
        facilityMap[fId].push(t);
      });
      Object.keys(facilityMap).forEach((fId) => {
        facilityMap[fId].sort((a, b) => (b.rating || 0) - (a.rating || 0));
      });
      for (const fId of Object.keys(facilityMap)) {
        const facilityName = getFacilityName(fId);
        facilityMap[fId].forEach((t) => {
          data.push([
            facilityName,
            formatDate(t.createdAt),
            t.title || '',
            formatDuration(t.duration),
            (t.rating || 0).toFixed(1),
          ]);
        });
      }
      const ws = XLSX.utils.aoa_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Testimonials');
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'testimonials_report.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Error generating report. Check console for details.');
    } finally {
      setIsLoading(false);
    }
  };

  const facilityOptions = [
    { id: 'all', name: 'All Facilities' },
    ...facilitiesData,
  ];

  return (
    <DashboardLayout
      facilityId={facilityId}
      isLoading={isLoading}
      setIsLoading={setIsLoading}
    >
      <Box sx={{ maxWidth: 1280, margin: '0 auto' }}>
        <div className={styles.testimonialsActionsContainer}>
          {isSuperAdmin && (
            <>
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => setOpenSettingsDialog(true)}
              >
                Manage
              </Button>
              <Button
                variant="outlined"
                startIcon={<ListAltIcon />}
                onClick={handleGenerateReport}
              >
                Report
              </Button>
            </>
          )}
          {canUploadTo(facilityId) && (
            <Button
              variant="contained"
              disableElevation
              startIcon={<UploadIcon />}
              onClick={handleOpenForm}
            >
              Submit Testimonial
            </Button>
          )}
        </div>
        <div className={styles.testimonialsFilterSortContainer}>
          <Box>Filter By:</Box>
          <FormControl>
            <InputLabel id="facility-label">Facility</InputLabel>
            <Select
              labelId="facility-label"
              value={selectedFacility}
              label="Facility"
              onChange={handleFacilityChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Facility selector' }}
            >
              {facilityOptions.map((facility) => (
                <MenuItem key={facility.id} value={facility.id}>
                  {facility.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Start Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <TextField
            label="End Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <Box sx={{ ml: 'auto' }}>Sort:</Box>
          <Select
            value={sortOption}
            onChange={handleSortChange}
            displayEmpty
            sx={{ border: 'none' }}
            inputProps={{ 'aria-label': 'Sort selector' }}
          >
            <MenuItem value="Building">Building</MenuItem>
            <MenuItem value="Date">Date</MenuItem>
            {canRate && <MenuItem value="Top">Rating</MenuItem>}
          </Select>
        </div>

        <Box sx={{ mt: 4 }}>
          {sortedTestimonials().map((testimonial) => {
            let avgRating = testimonial.rating || 0;
            if (testimonial.ratings) {
              const vals = Object.values(testimonial.ratings);
              if (vals.length > 0) {
                const sum = vals.reduce((a, b) => a + b, 0);
                avgRating = sum / vals.length;
                avgRating = Math.floor(avgRating * 2) / 2;
              }
            }
            return (
              <Box key={testimonial.id} className={styles.testimonialCard}>
                <Box className={styles.testimonialCardVideo}>
                  {testimonial.videoUrl && (
                    <video src={testimonial.videoUrl} controls />
                  )}
                </Box>
                <Box className={styles.testimonialCardDetails}>
                  <h3>{testimonial.title}</h3>
                  <p>
                    {testimonial.description.length > 250
                      ? testimonial.description.slice(0, 250) + '...'
                      : testimonial.description}
                  </p>
                  <p>
                    Uploaded on:{' '}
                    {testimonial.createdAt?.toDate
                      ? testimonial.createdAt.toDate().toLocaleDateString()
                      : 'Pending'}
                  </p>

                  <p>Facility: {testimonial.facilityId} </p>
                  {canRate && (
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                      <Rating value={avgRating} precision={0.5} readOnly />
                      <Box sx={{ ml: 1 }}>{avgRating.toFixed(1)}</Box>
                    </Box>
                  )}
                  <Box sx={{ pt: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(testimonial);
                      }}
                    >
                      Download
                    </Button>
                    {canEditTestimonial(testimonial) && (
                      <Button
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditTestimonial(testimonial);
                        }}
                        sx={{ ml: 1 }}
                      >
                        Edit
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
          {sortedTestimonials().length === 0 && (
            <p>No testimonials found for this facility.</p>
          )}
        </Box>
      </Box>

      <Dialog open={openForm} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle>Submit a Testimonial</DialogTitle>
        <DialogContent>
          <form ref={formRef} onSubmit={handleSubmit}>
            <TextField
              autoFocus
              margin="dense"
              label="Title"
              type="text"
              fullWidth
              variant="outlined"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
            <TextField
              margin="dense"
              label="Description"
              type="text"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              inputProps={{ maxLength: 250 }}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
            <Button
              variant="outlined"
              component="label"
              sx={{ marginTop: '1rem' }}
            >
              Select Video
              <input
                type="file"
                accept="video/*"
                hidden
                onChange={handleFileChange}
              />
            </Button>
            {videoFile && <p>Selected: {videoFile.name}</p>}
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(editTestimonial)}
        onClose={() => setEditTestimonial(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Testimonial</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            type="text"
            fullWidth
            variant="outlined"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            required
          />
          <TextField
            margin="dense"
            label="Description"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            inputProps={{ maxLength: 250 }}
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            required
          />
          {canRate && (
            <Box sx={{ mt: 2 }}>
              <p>Rate this testimonial:</p>
              <Rating
                name="editable-rating"
                precision={0.5}
                value={editRating}
                onChange={(event, newValue) => {
                  setEditRating(newValue);
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTestimonial(null)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save
          </Button>
          <Button onClick={handleDeleteEdit} color="error" variant="outlined">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <TestimonialSettingsDialog
        open={openSettingsDialog}
        onClose={() => setOpenSettingsDialog(false)}
        facilityId={
          selectedFacility !== 'all' ? selectedFacility : facilityId || ''
        }
      />
    </DashboardLayout>
  );
}

export default Testimonials;
